import { getHeaders } from "../request-headers.ts";
import type {
  BindExistingAccountRequest,
  CreateWechatUserRequest,
  HandleWechatBridgeRelayCallbackParams,
  LocalLoginVerificationRequest,
  OkResponse,
  SendWechatBindVerificationEmailRequest,
  SetLocalLoginForWechatRequest,
  WechatAuthUrlRequest,
  WechatAuthUrlResponse,
  WechatBindContextResponse,
  WechatBindStatusResponse,
  WechatBindTokenRequest,
  WechatLoginResponse,
} from "../../model";

import { cnapexApiFetch } from "../../../fetcher.ts";

export const getGetWechatAuthUrlUrl = () => {
  return `/api/auth/wechat/auth-url`;
};

export const getWechatAuthUrl = async (
  wechatAuthUrlRequest: WechatAuthUrlRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatAuthUrlResponse> => {
  return cnapexApiFetch<WechatAuthUrlResponse>(getGetWechatAuthUrlUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(wechatAuthUrlRequest),
  });
};

export const getGetWechatBindContextUrl = () => {
  return `/api/auth/wechat/bind-context`;
};

export const getWechatBindContext = async (
  wechatBindTokenRequest: WechatBindTokenRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatBindContextResponse> => {
  return cnapexApiFetch<WechatBindContextResponse>(getGetWechatBindContextUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(wechatBindTokenRequest),
  });
};

export const getBindExistingAccountUrl = () => {
  return `/api/auth/wechat/bind-existing-account`;
};

export const bindExistingAccount = async (
  bindExistingAccountRequest: BindExistingAccountRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatLoginResponse> => {
  return cnapexApiFetch<WechatLoginResponse>(getBindExistingAccountUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(bindExistingAccountRequest),
  });
};

export const getGetWechatBindStatusUrl = () => {
  return `/api/auth/wechat/bind-status`;
};

export const getWechatBindStatus = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatBindStatusResponse> => {
  return cnapexApiFetch<WechatBindStatusResponse>(getGetWechatBindStatusUrl(), {
    ...options,
    method: "GET",
  });
};

export const getSendWechatBindVerificationEmailUrl = () => {
  return `/api/auth/wechat/bind-verification-code`;
};

export const sendWechatBindVerificationEmail = async (
  sendWechatBindVerificationEmailRequest: SendWechatBindVerificationEmailRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getSendWechatBindVerificationEmailUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(sendWechatBindVerificationEmailRequest),
  });
};

export const getHandleWechatBridgeRelayCallbackUrl = (
  params?: HandleWechatBridgeRelayCallbackParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/auth/wechat/callback?${stringifiedParams}`
    : `/api/auth/wechat/callback`;
};

export const handleWechatBridgeRelayCallback = async (
  params?: HandleWechatBridgeRelayCallbackParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<unknown> => {
  return cnapexApiFetch<unknown>(getHandleWechatBridgeRelayCallbackUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getCreateWechatUserUrl = () => {
  return `/api/auth/wechat/create-user`;
};

export const createWechatUser = async (
  createWechatUserRequest: CreateWechatUserRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatLoginResponse> => {
  return cnapexApiFetch<WechatLoginResponse>(getCreateWechatUserUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(createWechatUserRequest),
  });
};

export const getSetLocalLoginForWechatUrl = () => {
  return `/api/auth/wechat/local-login`;
};

export const setLocalLoginForWechat = async (
  setLocalLoginForWechatRequest: SetLocalLoginForWechatRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getSetLocalLoginForWechatUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(setLocalLoginForWechatRequest),
  });
};

export const getSendLocalLoginVerificationEmailUrl = () => {
  return `/api/auth/wechat/local-login-verification-code`;
};

export const sendLocalLoginVerificationEmail = async (
  localLoginVerificationRequest: LocalLoginVerificationRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getSendLocalLoginVerificationEmailUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(localLoginVerificationRequest),
  });
};

export const getUnbindWechatUrl = () => {
  return `/api/auth/wechat/unbind`;
};

export const unbindWechat = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getUnbindWechatUrl(), {
    ...options,
    method: "POST",
  });
};
